import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  KeyRound, User, CheckCircle, AlertTriangle, XCircle,
  RefreshCw, ArrowRight, Shield, Loader, Users, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAuthBinding() {
  const [password, setPassword] = useState(sessionStorage.getItem('admin_password') || '');
  const [authenticated, setAuthenticated] = useState(!!sessionStorage.getItem('admin_password'));

  // Seed status
  const [seedStatus, setSeedStatus] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedError, setSeedError] = useState(null);

  // Dry run
  const [clerkId, setClerkId] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [dryRunError, setDryRunError] = useState(null);

  // Apply
  const [confirmText, setConfirmText] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState(null);

  // Clerk-linked users
  const [linkedUsers, setLinkedUsers] = useState(null);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkedError, setLinkedError] = useState(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Admin-Password': password || sessionStorage.getItem('admin_password') || '',
  }), [password]);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('admin_password');
    setPassword('');
    setAuthenticated(false);
    setSeedStatus(null);
    setLinkedUsers(null);
  }, []);

  const fetchSeedStatus = useCallback(async () => {
    setSeedLoading(true);
    setSeedError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/auth/seed-binding/status`, { headers: headers() });
      if (resp.status === 401) { handleLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setSeedStatus(data);
    } catch (e) {
      setSeedError(e.message);
    } finally {
      setSeedLoading(false);
    }
  }, [headers, handleLogout]);

  const fetchLinkedUsers = useCallback(async () => {
    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/auth/clerk-linked-users`, { headers: headers() });
      if (resp.status === 401) { handleLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setLinkedUsers(data);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  }, [headers, handleLogout]);

  const handleLogin = (e) => {
    e.preventDefault();
    sessionStorage.setItem('admin_password', password);
    setAuthenticated(true);
  };

  useEffect(() => {
    if (authenticated) {
      fetchSeedStatus();
      fetchLinkedUsers();
    }
  }, [authenticated, fetchSeedStatus, fetchLinkedUsers]);

  const runDryRun = async () => {
    setDryRunResult(null);
    setDryRunError(null);
    setDryRunLoading(true);
    setApplyResult(null);
    setApplyError(null);
    setConfirmText('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/auth/seed-binding/dry-run`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ production_clerk_user_id: clerkId.trim() }),
      });
      if (resp.status === 401) { handleLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setDryRunResult(data);
    } catch (e) {
      setDryRunError(e.message);
    } finally {
      setDryRunLoading(false);
    }
  };

  const runApply = async () => {
    setApplyResult(null);
    setApplyError(null);
    setApplyLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/auth/seed-binding/apply`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          production_clerk_user_id: clerkId.trim(),
          confirmation: confirmText,
        }),
      });
      if (resp.status === 401) { handleLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setApplyResult(data);
      if (data.applied) {
        fetchSeedStatus();
        fetchLinkedUsers();
      }
    } catch (e) {
      setApplyError(e.message);
    } finally {
      setApplyLoading(false);
    }
  };

  const canApply =
    dryRunResult &&
    dryRunResult.valid &&
    !dryRunResult.already_bound &&
    confirmText === 'BIND MAGIC FOREST';

  // ── Login gate ──
  if (!authenticated) {
    return (
      <div style={styles.loginRoot}>
        <form onSubmit={handleLogin} style={styles.loginCard}>
          <KeyRound size={28} color="#e8a012" />
          <h2 style={styles.loginTitle}>Auth Binding</h2>
          <input
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
          />
          <button type="submit" style={styles.btnPrimary}>View Status</button>
        </form>
      </div>
    );
  }

  return (
    <AdminLayout activePage="auth-binding" onLogout={handleLogout}>
      <div style={styles.page}>
        <div style={styles.headerRow}>
          <h1 style={styles.pageTitle}>
            <KeyRound size={22} color="#e8a012" style={{ marginRight: 8 }} />
            Seed User Auth Binding
          </h1>
          <button onClick={() => { fetchSeedStatus(); fetchLinkedUsers(); }} style={styles.btnGhost}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div style={styles.banner}>
          <Shield size={14} color="#e8a012" />
          <span>Admin-only tool. Binds the Magic Forest seed user to a production Clerk user ID. Does NOT move content.</span>
        </div>

        {/* ── Section 1: Seed User Status ── */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <User size={16} color="#a3a3a3" /> Seed User Status
          </h2>
          {seedLoading && <div style={styles.loading}><Loader size={14} className="spin" /> Loading...</div>}
          {seedError && <div style={styles.errorBox}>{seedError}</div>}
          {seedStatus && seedStatus.found && (
            <div>
              <div style={styles.statusBadge(seedStatus.binding_status === 'bound')}>
                {seedStatus.binding_status === 'bound' ? (
                  <><CheckCircle size={14} /> Bound to Clerk</>
                ) : (
                  <><AlertTriangle size={14} /> Unbound (legacy_local)</>
                )}
              </div>
              <table style={styles.table}>
                <tbody>
                  {Object.entries(seedStatus.user).map(([key, val]) => (
                    <tr key={key}>
                      <td style={styles.tdLabel}>{key}</td>
                      <td style={styles.tdValue}>{val === null ? <span style={styles.null}>null</span> : String(val)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={styles.tdLabel}>carousels</td>
                    <td style={styles.tdValue}>{seedStatus.content_counts.carousels}</td>
                  </tr>
                  <tr>
                    <td style={styles.tdLabel}>media_assets</td>
                    <td style={styles.tdValue}>{seedStatus.content_counts.media_assets}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {seedStatus && !seedStatus.found && (
            <div style={styles.errorBox}>{seedStatus.error}</div>
          )}
        </div>

        {/* ── Section 2: Bind to Clerk ── */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <KeyRound size={16} color="#e8a012" /> Bind to Production Clerk User
          </h2>

          <div style={styles.inputRow}>
            <label style={styles.label}>Production Clerk User ID</label>
            <input
              type="text"
              placeholder="user_2abc123def456..."
              value={clerkId}
              onChange={(e) => { setClerkId(e.target.value); setDryRunResult(null); setApplyResult(null); }}
              style={{ ...styles.input, flex: 1 }}
            />
            <button
              onClick={runDryRun}
              disabled={!clerkId.trim() || dryRunLoading}
              style={{
                ...styles.btnSecondary,
                opacity: (!clerkId.trim() || dryRunLoading) ? 0.5 : 1,
              }}
            >
              {dryRunLoading ? <><Loader size={14} className="spin" /> Checking...</> : 'Dry Run'}
            </button>
          </div>

          {dryRunError && <div style={styles.errorBox}>{dryRunError}</div>}

          {/* Dry run result */}
          {dryRunResult && (
            <div style={styles.dryRunBox(dryRunResult.valid)}>
              {/* Status header */}
              <div style={styles.dryRunHeader}>
                {dryRunResult.valid ? (
                  dryRunResult.already_bound ? (
                    <><CheckCircle size={16} color="#4ade80" /> Already bound — no action needed</>
                  ) : (
                    <><CheckCircle size={16} color="#4ade80" /> Validation passed — ready to apply</>
                  )
                ) : (
                  <><XCircle size={16} color="#f87171" /> {dryRunResult.message || dryRunResult.error}</>
                )}
              </div>

              {/* Proposed changes */}
              {dryRunResult.proposed_changes && dryRunResult.proposed_changes.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={styles.subLabel}>Will change:</div>
                  <table style={styles.changeTable}>
                    <thead>
                      <tr>
                        <th style={styles.changeTh}>Field</th>
                        <th style={styles.changeTh}>From</th>
                        <th style={styles.changeTh}></th>
                        <th style={styles.changeTh}>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRunResult.proposed_changes.map((c, i) => (
                        <tr key={i}>
                          <td style={styles.changeTd}><code style={styles.code}>{c.field}</code></td>
                          <td style={styles.changeTd}>
                            {c.from === null ? <span style={styles.null}>null</span> : String(c.from)}
                          </td>
                          <td style={{ ...styles.changeTd, textAlign: 'center' }}>
                            <ArrowRight size={12} color="#a3a3a3" />
                          </td>
                          <td style={{ ...styles.changeTd, color: '#4ade80' }}>{String(c.to)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Will NOT change */}
              {dryRunResult.will_not_change && (
                <div style={{ marginTop: 12 }}>
                  <div style={styles.subLabel}>Will NOT change:</div>
                  <div style={styles.noChangeList}>
                    {dryRunResult.will_not_change.map((item, i) => (
                      <span key={i} style={styles.noChangeBadge}>{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Conflicting user */}
              {dryRunResult.conflicting_user && (
                <div style={{ marginTop: 12 }}>
                  <div style={styles.subLabel}>Conflicting user:</div>
                  <div style={styles.conflictRow}>
                    {dryRunResult.conflicting_user.id} — {dryRunResult.conflicting_user.email || 'no email'}
                    {dryRunResult.conflicting_user.is_anonymous && ' (anonymous)'}
                  </div>
                </div>
              )}

              {/* Confirmation & Apply (only if valid and not already bound) */}
              {dryRunResult.valid && !dryRunResult.already_bound && (
                <div style={styles.applySection}>
                  <div style={styles.confirmRow}>
                    <label style={styles.label}>
                      Type <code style={styles.codeInline}>BIND MAGIC FOREST</code> to confirm:
                    </label>
                    <input
                      type="text"
                      placeholder="BIND MAGIC FOREST"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      style={{ ...styles.input, flex: 1, maxWidth: 280 }}
                    />
                    <button
                      onClick={runApply}
                      disabled={!canApply || applyLoading}
                      style={{
                        ...styles.btnDanger,
                        opacity: (!canApply || applyLoading) ? 0.4 : 1,
                      }}
                    >
                      {applyLoading ? <><Loader size={14} className="spin" /> Applying...</> : 'Apply Binding'}
                    </button>
                  </div>
                </div>
              )}

              {/* Apply result */}
              {applyError && <div style={styles.errorBox}>{applyError}</div>}
              {applyResult && (
                <div style={styles.applyResultBox(applyResult.applied)}>
                  {applyResult.applied ? (
                    <>
                      <CheckCircle size={16} color="#4ade80" />
                      <span>
                        {applyResult.idempotent
                          ? 'Already bound (idempotent)'
                          : 'Seed user successfully bound to Clerk!'}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle size={16} color="#f87171" />
                      <span>{applyResult.message || applyResult.error}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Section 3: Clerk-Linked Users ── */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <Users size={16} color="#a3a3a3" /> Users with Clerk IDs
            {linkedUsers && <span style={styles.countBadge}>{linkedUsers.count}</span>}
          </h2>
          <div style={styles.readOnlyBanner}>
            <Shield size={12} /> Read-only. Review before switching to production Clerk.
          </div>

          {linkedLoading && <div style={styles.loading}><Loader size={14} className="spin" /> Loading...</div>}
          {linkedError && <div style={styles.errorBox}>{linkedError}</div>}

          {linkedUsers && linkedUsers.count === 0 && (
            <div style={styles.emptyState}>No users currently linked to Clerk.</div>
          )}

          {linkedUsers && linkedUsers.count > 0 && (
            <div style={styles.tableWrap}>
              <table style={styles.fullTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Auth Provider</th>
                    <th style={styles.th}>Clerk User ID</th>
                    <th style={styles.th}>Carousels</th>
                    <th style={styles.th}>Assets</th>
                    <th style={styles.th}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedUsers.users.map((u) => (
                    <tr key={u.id}>
                      <td style={styles.td}>{u.email || <span style={styles.null}>null</span>}</td>
                      <td style={styles.td}>{u.name || '—'}</td>
                      <td style={styles.td}>
                        <span style={styles.providerBadge(u.auth_provider)}>{u.auth_provider}</span>
                      </td>
                      <td style={styles.td}>
                        <code style={styles.clerkIdCell}>{u.clerk_user_id}</code>
                      </td>
                      <td style={styles.tdNum}>{u.carousel_count}</td>
                      <td style={styles.tdNum}>{u.media_asset_count}</td>
                      <td style={styles.td}>{u.updated_at ? new Date(u.updated_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ── Styles ──
const styles = {
  loginRoot: {
    minHeight: '100vh',
    background: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginCard: {
    background: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: 12,
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    width: 340,
  },
  loginTitle: {
    color: '#e5e5e5',
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  page: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pageTitle: {
    color: '#e5e5e5',
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  banner: {
    background: '#1a1400',
    border: '1px solid #443200',
    borderRadius: 8,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#e8a012',
    fontSize: 13,
  },
  card: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 10,
    padding: '20px 22px',
  },
  cardTitle: {
    color: '#d4d4d4',
    fontSize: 15,
    fontWeight: 600,
    margin: '0 0 14px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  statusBadge: (bound) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 12,
    background: bound ? '#052e16' : '#1c1917',
    border: `1px solid ${bound ? '#166534' : '#854d0e'}`,
    color: bound ? '#4ade80' : '#fbbf24',
  }),
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  tdLabel: {
    padding: '5px 12px 5px 0',
    color: '#737373',
    fontFamily: 'IBM Plex Mono, monospace',
    fontSize: 12,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  },
  tdValue: {
    padding: '5px 0',
    color: '#d4d4d4',
    fontFamily: 'IBM Plex Mono, monospace',
    fontSize: 12,
    wordBreak: 'break-all',
  },
  null: { color: '#525252', fontStyle: 'italic' },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
  },
  label: {
    color: '#a3a3a3',
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 4,
    display: 'block',
  },
  input: {
    background: '#0a0a0a',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#e5e5e5',
    fontSize: 13,
    fontFamily: 'IBM Plex Mono, monospace',
    outline: 'none',
    minWidth: 200,
  },
  btnPrimary: {
    background: '#e8a012',
    color: '#000',
    border: 'none',
    borderRadius: 6,
    padding: '8px 20px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSecondary: {
    background: '#262626',
    color: '#e5e5e5',
    border: '1px solid #404040',
    borderRadius: 6,
    padding: '8px 16px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  btnDanger: {
    background: '#7f1d1d',
    color: '#fca5a5',
    border: '1px solid #991b1b',
    borderRadius: 6,
    padding: '8px 16px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  btnGhost: {
    background: 'transparent',
    color: '#737373',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 12,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  dryRunBox: (valid) => ({
    marginTop: 16,
    padding: '14px 16px',
    borderRadius: 8,
    border: `1px solid ${valid ? '#166534' : '#991b1b'}`,
    background: valid ? '#052e1680' : '#1c0a0a80',
  }),
  dryRunHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    color: '#e5e5e5',
  },
  subLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#737373',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 6,
  },
  changeTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  changeTh: {
    textAlign: 'left',
    padding: '4px 8px',
    fontSize: 11,
    color: '#525252',
    fontWeight: 500,
    borderBottom: '1px solid #262626',
  },
  changeTd: {
    padding: '4px 8px',
    fontSize: 12,
    color: '#a3a3a3',
    fontFamily: 'IBM Plex Mono, monospace',
  },
  code: {
    background: '#1a1a1a',
    padding: '1px 5px',
    borderRadius: 3,
    fontSize: 11,
    color: '#e8a012',
  },
  codeInline: {
    background: '#1a1a1a',
    padding: '1px 5px',
    borderRadius: 3,
    fontSize: 12,
    color: '#e8a012',
    fontFamily: 'IBM Plex Mono, monospace',
  },
  noChangeList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  noChangeBadge: {
    background: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    color: '#737373',
  },
  conflictRow: {
    background: '#1c0a0a',
    border: '1px solid #991b1b',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 12,
    color: '#fca5a5',
    fontFamily: 'IBM Plex Mono, monospace',
  },
  applySection: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: '1px solid #262626',
  },
  confirmRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
  },
  applyResultBox: (applied) => ({
    marginTop: 12,
    padding: '10px 14px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 500,
    background: applied ? '#052e16' : '#1c0a0a',
    border: `1px solid ${applied ? '#166534' : '#991b1b'}`,
    color: applied ? '#4ade80' : '#fca5a5',
  }),
  readOnlyBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 6,
    background: '#1a1a1a',
    border: '1px solid #262626',
    fontSize: 11,
    color: '#737373',
    marginBottom: 12,
  },
  countBadge: {
    background: '#262626',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: 11,
    color: '#a3a3a3',
    marginLeft: 8,
  },
  loading: {
    color: '#737373',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 0',
  },
  errorBox: {
    background: '#1c0a0a',
    border: '1px solid #991b1b',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 12,
    color: '#fca5a5',
    marginTop: 8,
  },
  emptyState: {
    color: '#525252',
    fontSize: 13,
    padding: '12px 0',
    textAlign: 'center',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  fullTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    fontSize: 11,
    color: '#525252',
    fontWeight: 600,
    borderBottom: '1px solid #262626',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  td: {
    padding: '6px 10px',
    color: '#a3a3a3',
    borderBottom: '1px solid #1a1a1a',
  },
  tdNum: {
    padding: '6px 10px',
    color: '#a3a3a3',
    borderBottom: '1px solid #1a1a1a',
    textAlign: 'center',
    fontFamily: 'IBM Plex Mono, monospace',
  },
  providerBadge: (provider) => ({
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    background: provider === 'clerk' ? '#052e16' : '#1a1a1a',
    color: provider === 'clerk' ? '#4ade80' : '#737373',
    border: `1px solid ${provider === 'clerk' ? '#166534' : '#333'}`,
  }),
  clerkIdCell: {
    fontSize: 11,
    color: '#e8a012',
    fontFamily: 'IBM Plex Mono, monospace',
    wordBreak: 'break-all',
  },
};
